import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { routeNames } from 'src/app/app-routing-names';
import { AuthType } from 'src/app/models/enums/auth-type';
import { AuthenticationService } from '../services/authentication/authentication.service';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { MotorQuoteStateService } from '../services/motor-quote-state/motor-quote-state.service';
import { QuoteSteps } from '../models/enums/quote-steps';
import { SessionIdService } from '../services/session-id/session-id.service';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationGuard {
    constructor(private readonly authenticationService: AuthenticationService,
        private readonly motorQuoteStateService: MotorQuoteStateService,
        private readonly sessionIdService: SessionIdService,
        private readonly router: Router) {
        dayjs.extend(isSameOrAfter);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.isAllowedNavigate(state);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        return this.canActivate(route, state);
    }

    async isAllowedNavigate(state: RouterStateSnapshot): Promise<boolean> {
        let hasValidAuth = this.sessionIdService.hasAuthToken();
        const authTime: Date = this.sessionIdService.getAuthTime();
        const authType = this.sessionIdService.getAuthType()
        const currentStep = this.motorQuoteStateService.getCurrentStep();

        if (hasValidAuth && authType === undefined) {
            this.sessionIdService.setAuthType(AuthType.Internal);
        }

        // If the token is from the day before and the user is marked internal
        // invalidate the auth and force the user to the login page to avoid and error message being shown
        if (hasValidAuth && authTime &&
            authType === AuthType.Internal &&
            !this.datesAreOnSameDay(new Date(authTime))) {
            hasValidAuth = false
        }

        if (hasValidAuth && !this.sessionIdService.hasValidAuthTime() && currentStep <= QuoteSteps.quoteDetails) {
            this.authenticationService.getAuthTokenForExternal()
        }

        if (hasValidAuth && (state.url.includes(routeNames.LOGIN) && authType !== AuthType.External)) {
            this.authenticationService.setLoginType(AuthType.Internal);
            this.motorQuoteStateService.setStepState(QuoteSteps.selectProduct)
        } else if (!hasValidAuth && !state.url.includes(routeNames.LOGIN)) {
            this.authenticationService.setLoginType();

            this.router.navigate([routeNames.LOGIN], {
                queryParams: {
                    return: state.url
                }
            });
        } else if (!hasValidAuth && state.url.includes(routeNames.LOGIN)) {
            return firstValueFrom(of(true));
        }

        if (!state.url.includes(routeNames.LOGIN) && currentStep < QuoteSteps.quoteDetails) {
            this.authenticationService.setLoginType(authType);
            this.authenticationService.monitorForActivityEvents();
        } else {
            this.authenticationService.unsubscribeFromActivitySub();
        }
        return firstValueFrom(of(hasValidAuth));
    }

    private datesAreOnSameDay(first: Date, second: Date = new Date()): boolean {
        return first.getFullYear() === second.getFullYear() &&
            first.getMonth() === second.getMonth() &&
            first.getDate() === second.getDate();
    }
}
